import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, Loader2, CreditCard, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import SEO from '../../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#D4AF37'
};

const HiveSubscribePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState('');
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('hive_token');
    const storedUser = localStorage.getItem('hive_user');
    
    if (!token || !storedUser) {
      navigate('/hive/login');
      return;
    }
    
    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // If active subscription, go to dashboard
    if (userData.subscription_status === 'active') {
      navigate('/hive/dashboard');
    }
  }, [navigate]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowDisclosure(true);
  };

  const handleConfirmSubscription = async () => {
    if (!selectedPlan) return;
    
    setIsLoading(true);
    setLoadingPlan(selectedPlan);
    setError('');
    
    const token = localStorage.getItem('hive_token');
    
    try {
      const res = await axios.post(
        `${API}/hive/checkout`,
        { origin_url: window.location.origin, plan: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
      setIsLoading(false);
      setLoadingPlan(null);
      setShowDisclosure(false);
    }
  };

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 19.99,
      interval: 'month',
      features: [
        'Full access to your tier features',
        'Load tracking dashboard',
        'Document management',
        'Direct communication with dispatcher'
      ]
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 199.99,
      interval: 'year',
      savings: 'Save $29.89',
      popular: true,
      features: [
        'Full access to your tier features',
        'Load tracking dashboard',
        'Document management',
        'Direct communication with dispatcher'
      ]
    }
  ];

  const tierColor = TIER_COLORS[user?.dispatch_tier] || '#D4AF37';

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6" data-testid="hive-subscribe-page">
      <SEO 
        title="Subscribe to The Hive"
        description="Complete your subscription to continue accessing The Hive carrier portal."
        canonical="https://thehive.queenblogistics.com/hive/subscribe"
      />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37] mb-6">
            <Crown className="w-8 h-8 text-[#D4AF37]" />
          </div>
          
          {user?.subscription_status === 'suspended' ? (
            <>
              <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-2">
                Payment Required
              </h1>
              <p className="text-white/60">
                Your trial has ended or payment failed. Subscribe to continue access.
              </p>
            </>
          ) : user?.subscription_status === 'canceled' ? (
            <>
              <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-2">
                Welcome <span className="text-[#D4AF37]">Back</span>
              </h1>
              <p className="text-white/60">
                {user?.first_name}, your subscription was canceled. Reactivate to regain access.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-2">
                Complete Your <span className="text-[#D4AF37]">Subscription</span>
              </h1>
              <p className="text-white/60">
                {user?.first_name}, choose your billing plan to continue
              </p>
            </>
          )}
          
          {user?.dispatch_tier && (
            <div 
              className="inline-flex items-center gap-2 mt-4 px-4 py-2"
              style={{ backgroundColor: `${tierColor}20`, border: `1px solid ${tierColor}` }}
            >
              <Crown className="w-4 h-4" style={{ color: tierColor }} />
              <span style={{ color: tierColor }} className="font-bold uppercase">
                {user.dispatch_tier} Tier
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-[#121212] border p-6 ${
                plan.popular ? 'border-[#D4AF37]' : 'border-white/10'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black px-4 py-1 text-xs font-bold uppercase">
                  Best Value
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-['Barlow_Condensed'] text-5xl font-bold text-[#D4AF37]">
                    ${plan.price}
                  </span>
                  <span className="text-white/50">/{plan.interval}</span>
                </div>
                {plan.savings && (
                  <p className="text-green-400 text-sm mt-1">{plan.savings}</p>
                )}
              </div>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-[#D4AF37]" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={isLoading}
                className={`w-full py-3 font-bold uppercase tracking-wider transition-colors ${
                  plan.popular
                    ? 'bg-[#D4AF37] text-black hover:bg-[#B8960D]'
                    : 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10'
                }`}
                data-testid={`select-${plan.id}`}
              >
                {loadingPlan === plan.id ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Select Plan'
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <button 
            onClick={() => {
              localStorage.removeItem('hive_token');
              localStorage.removeItem('hive_user');
              navigate('/hive/login');
            }}
            className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
          >
            Sign out and go back
          </button>
        </div>
      </div>

      {/* Billing Disclosure Modal */}
      {showDisclosure && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-50">
          <div className="bg-[#121212] border border-[#D4AF37] max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#D4AF37]" />
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase">
                Billing Disclosure
              </h3>
            </div>
            
            <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-6">
              <p className="text-white/80 text-sm leading-relaxed">
                This includes a <strong className="text-[#D4AF37]">14-day free trial</strong>. 
                Your subscription will automatically begin at{' '}
                <strong className="text-white">
                  ${selectedPlan === 'yearly' ? '199.99/year' : '19.99/month'}
                </strong>{' '}
                after the trial ends unless canceled before that date. 
                Your payment method will be charged automatically.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowDisclosure(false);
                  setSelectedPlan(null);
                }}
                className="flex-1 py-3 border border-white/30 text-white/70 font-bold uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubscription}
                disabled={isLoading}
                className="flex-1 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8960D] transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-subscription"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Confirm
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiveSubscribePage;
